import { useMutation, useQuery } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { queryClient } from "@via/api/query-client.tsx";
import type {
  CompleteOAuthConnectionRequestBody,
  ListOAuthConnectionsResponse,
  ListOAuthSharedDrivesResponse,
  OAuthConnectionSummary,
  OAuthStorageProvider,
  StartOAuthConnectionResponse,
  UpdateOAuthConnectionRequestBody,
} from "common";

/** Google Drive and Dropbox connections (data-2-1). No response ever carries a grant. */
export const OAUTH_CONNECTIONS_KEY = [
  "/api/v1/remote-storage/oauth/connections",
];

const invalidate = () =>
  queryClient.invalidateQueries({ queryKey: OAUTH_CONNECTIONS_KEY });

export const useOAuthConnections = () =>
  useQuery({
    queryKey: OAUTH_CONNECTIONS_KEY,
    queryFn: async (): Promise<ListOAuthConnectionsResponse> =>
      (await axios.get("/api/v1/remote-storage/oauth/connections")).data,
  });

export const useStartOAuthConnection = () =>
  useMutation({
    mutationFn: async (
      provider: OAuthStorageProvider
    ): Promise<StartOAuthConnectionResponse> =>
      (await axios.post("/api/v1/remote-storage/oauth/start", { provider }))
        .data,
  });

export const useCompleteOAuthConnection = () =>
  useMutation({
    mutationFn: async (
      body: CompleteOAuthConnectionRequestBody
    ): Promise<OAuthConnectionSummary> =>
      (await axios.post("/api/v1/remote-storage/oauth/complete", body)).data,
    onSuccess: invalidate,
  });

export const useUpdateOAuthConnection = () =>
  useMutation({
    mutationFn: async ({
      id,
      body,
    }: {
      id: number;
      body: UpdateOAuthConnectionRequestBody;
    }): Promise<OAuthConnectionSummary> =>
      (await axios.put(`/api/v1/remote-storage/oauth/connections/${id}`, body))
        .data,
    onSuccess: invalidate,
  });

export const useDeleteOAuthConnection = () =>
  useMutation({
    mutationFn: async (id: number) =>
      (await axios.delete(`/api/v1/remote-storage/oauth/connections/${id}`))
        .data,
    onSuccess: invalidate,
  });

export const useOAuthSharedDrives = (id: number, enabled: boolean) =>
  useQuery({
    queryKey: [...OAUTH_CONNECTIONS_KEY, id, "shared-drives"],
    queryFn: async (): Promise<ListOAuthSharedDrivesResponse> =>
      (
        await axios.get(
          `/api/v1/remote-storage/oauth/connections/${id}/shared-drives`
        )
      ).data,
    enabled,
  });

// The dataset the sign in started from, so the way back from the provider returns to it. Only a
// convenience: without storage the user lands on the browser without a dataset and picks again.
const RETURN_KEY = "fc-remote-storage-oauth-return";

export const rememberOAuthReturn = (datasetId: string | null): void => {
  try {
    if (datasetId) window.sessionStorage.setItem(RETURN_KEY, datasetId);
    else window.sessionStorage.removeItem(RETURN_KEY);
  } catch {
    // storage blocked: nothing to remember
  }
};

export const peekOAuthReturn = (): string | null => {
  try {
    return window.sessionStorage.getItem(RETURN_KEY);
  } catch {
    return null;
  }
};

export const takeOAuthReturn = (): string | null => {
  try {
    const value = window.sessionStorage.getItem(RETURN_KEY);
    window.sessionStorage.removeItem(RETURN_KEY);
    return value;
  } catch {
    return null;
  }
};
